// src/components/Reports.js
import React, { useState, useRef, useMemo, useEffect } from "react";
import "../assets/styles/PushNotification.css";
import { Modal, Form, Spinner } from "react-bootstrap";
import { createPortal } from "react-dom";
import notificationService from "../../services/notificationService";

// Full view icons
import fullpush from "../assets/img/fullpush.svg";
import fullpushclicked from "../assets/img/fullpushclicked.svg";
import fullpushcredict from "../assets/img/fullpushcredit.svg";
import fullpushdeliver from "../assets/img/fullpushdelivered.svg";
import fullpushopened from "../assets/img/fullpushopened.svg";
import fullpushsubscription from "../assets/img/fullpushsubscription.svg";

// Breakdown icons
import push from "../assets/img/push.svg";
import pushclicked from "../assets/img/pushclicked.svg";
import pushcredict from "../assets/img/pushcredit.svg";
import pushdeliver from "../assets/img/pushdelivered.svg";
import pushopen from "../assets/img/pushopened.svg";
import pushsubscription from "../assets/img/pushsubscription.svg";
import reportclock from "../assets/img/report-clock.svg";
import up from "../assets/img/up.svg";
import down from "../assets/img/down.svg";
import close from "../assets/img/close-square.svg";
import calendar from "../assets/img/calendar.svg";
import filter1 from "../assets/img/filter1.svg"

const PushNotificationReports = () => {
  const [activeFilter, setActiveFilter] = useState("This Year");
  const [openIndex, setOpenIndex] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // API data states
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportStats, setReportStats] = useState({
    totalPush: 0,
    creditsUsed: 0,
    totalDelivered: 0,
    totalOpened: 0,
    totalClicked: 0,
    totalRegistrations: 0,
  });

  // Dashboard-style calendar + dropdown states
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0, width: 0 });
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    reportType: [],
    successRate: "",
  });
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
  const [currentDateType, setCurrentDateType] = useState("");
  const [selectedDayIdx, setSelectedDayIdx] = useState(0);
  const [selectedMonthIdx, setSelectedMonthIdx] = useState(0);
  const [selectedYearIdx, setSelectedYearIdx] = useState(0);
  const [openDropdown, setOpenDropdown] = useState(null);

  const toggleDropdown = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const startYear = 2015;
  const endYear = new Date().getFullYear() + 20;
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => (startYear + i).toString());

  const getDaysInMonth = (monthIdx, year) => new Date(year, monthIdx + 1, 0).getDate();

  const days = useMemo(() => {
    const y = parseInt(years[selectedYearIdx] || new Date().getFullYear());
    const m = selectedMonthIdx >= 0 ? selectedMonthIdx : new Date().getMonth();
    const totalDays = getDaysInMonth(m, y);
    if (selectedDayIdx >= totalDays) setSelectedDayIdx(totalDays - 1);
    return Array.from({ length: totalDays }, (_, i) => (i + 1).toString().padStart(2, "0"));
  }, [selectedMonthIdx, selectedYearIdx, years, selectedDayIdx]);

  const dayRef = useRef(null);
  const monthRef = useRef(null);
  const yearRef = useRef(null);

  const openDatePicker = (type, e) => {
    setCurrentDateType(type);
    const rect = e.currentTarget.getBoundingClientRect();
    setPickerPosition({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });

    const savedDate = filters[type];
    let dayIdx, monthIdx, yearIdx;
    if (savedDate) {
      const [d, m, y] = savedDate.split(" ");
      dayIdx = parseInt(d, 10) - 1;
      monthIdx = months.indexOf(m);
      yearIdx = years.indexOf(y);
    } else {
      const today = new Date();
      dayIdx = today.getDate() - 1;
      monthIdx = today.getMonth();
      yearIdx = years.indexOf(today.getFullYear().toString());
    }

    setSelectedDayIdx(dayIdx >= 0 ? dayIdx : 0);
    setSelectedMonthIdx(monthIdx >= 0 ? monthIdx : 0);
    setSelectedYearIdx(yearIdx >= 0 ? yearIdx : 0);
    setIsDatePickerVisible(true);
  };

  // Scroll to the selected date only when the picker opens. Re-running this on every
  // index change fights the user's scroll (and CSS snap) and makes the wheel jump.
  useEffect(() => {
    if (!isDatePickerVisible) return;
    const ITEM_HEIGHT = 40;
    const scrollTo = (ref, index) => {
      if (!ref.current) return;
      ref.current.scrollTop = index * ITEM_HEIGHT;
    };
    requestAnimationFrame(() => {
      scrollTo(dayRef, selectedDayIdx);
      scrollTo(monthRef, selectedMonthIdx);
      scrollTo(yearRef, selectedYearIdx);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDatePickerVisible]);

  const handleScroll = (ref, setIndex, itemCount) => {
    if (!ref.current) return;
    const ITEM_HEIGHT = 40;
    const { scrollTop } = ref.current;
    const index = Math.round(scrollTop / ITEM_HEIGHT);
    if (index >= 0 && index < itemCount) setIndex(index);
  };

  const handleSaveDate = () => {
    const day = days[selectedDayIdx];
    const month = months[selectedMonthIdx];
    const year = years[selectedYearIdx];
    setFilters((prev) => ({ ...prev, [currentDateType]: `${day} ${month} ${year}` }));
    setIsDatePickerVisible(false);
  };

  const handleClearFilters = () => {
    setFilters({ startDate: "", endDate: "", reportType: [], successRate: "" });
    loadReports();
  };

  // Load reports from API
  const loadReports = async () => {
    try {
      setLoading(true);
      console.log('📊 Loading reports with filters:', {
        period: activeFilter,
        dateRange: { from: filters.startDate, to: filters.endDate },
        reportType: filters.reportType,
        status: filters.successRate,
      });

      const response = await notificationService.getNotificationReports({
        period: activeFilter,
        dateRange: { from: filters.startDate, to: filters.endDate },
        reportType: filters.reportType,
        status: filters.successRate,
      });

      console.log('📊 Reports API Response:', response);

      if (response.success && response.data) {
        console.log('✅ Reports loaded successfully:', {
          reportsCount: response.data.reports?.length || 0,
          stats: response.data,
        });
        setReports(response.data.reports || []);
        setReportStats({
          totalPush: response.data.totalPush || 0,
          creditsUsed: response.data.creditsUsed || 0,
          totalDelivered: response.data.totalDelivered || 0,
          totalOpened: response.data.totalOpened || 0,
          totalClicked: response.data.totalClicked || 0,
          totalRegistrations: response.data.totalRegistrations || 0,
        });
      } else {
        console.log('⚠️ No data in API response:', response);
        // No data from API - keep empty state (like app)
        setReports([]);
        setReportStats({
          totalPush: 0,
          creditsUsed: 0,
          totalDelivered: 0,
          totalOpened: 0,
          totalClicked: 0,
          totalRegistrations: 0,
        });
      }
    } catch (err) {
      console.error('❌ Error loading reports:', err);
      console.error('Error details:', JSON.stringify(err, null, 2));
      // On error - keep empty state (like app)
      setReports([]);
      setReportStats({
        totalPush: 0,
        creditsUsed: 0,
        totalDelivered: 0,
        totalOpened: 0,
        totalClicked: 0,
        totalRegistrations: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // Load reports when filter changes
  useEffect(() => {
    loadReports();
  }, [activeFilter]);

  // Fallback static data when API is not available
  const fallbackStats = {
    totalPush: 423,
    creditsUsed: 1245,
    totalDelivered: 219,
    totalOpened: 754,
    totalClicked: 672,
    totalRegistrations: 1234,
  };

  const fallbackBreakdown = [
    {
      key: "Agent",
      title: "Agent",
      notification: "Project Launch Notification",
      sentTo: "All Subscribed Agents",
      rate: "8.7%",
      date: "12/06/2025, 11:25am",
      stats: [
        { label: "Push Sent", value: "245", image: push },
        { label: "Credits Used", value: "232", image: pushcredict },
        { label: "Delivered", value: "2335", image: pushdeliver },
        { label: "Opened", value: "3445", image: pushopen },
        { label: "Clicked", value: "24445", image: pushclicked },
        { label: "Registrations", value: "24445", image: pushsubscription },
      ],
    },
  ];

  // Dynamic stats array based on API data
  const stats = [
    { label: "No of Push", value: reportStats.totalPush, icon: fullpush },
    { label: "Credits Used", value: reportStats.creditsUsed, icon: fullpushcredict },
    { label: "Total Delivered", value: reportStats.totalDelivered, icon: fullpushdeliver },
    { label: "Total Opened", value: reportStats.totalOpened, icon: fullpushopened },
    { label: "Total Clicked", value: reportStats.totalClicked, icon: fullpushclicked },
    { label: "Total Subscriptions", value: reportStats.totalRegistrations, icon: fullpushsubscription },
  ];

  // Use API data if available, fallback to static data
  const breakdown = reports.length > 0 ? reports : fallbackBreakdown;

  // Handle report type filter toggle
  const toggleReportType = (type) => {
    setFilters(prev => ({
      ...prev,
      reportType: prev.reportType.includes(type)
        ? prev.reportType.filter(item => item !== type)
        : [...prev.reportType, type]
    }));
  };

  // Apply filters and reload reports
  const applyFilters = async () => {
    setShowModal(false);
    await loadReports();
  };

  return (
    <div className="reports-section">
      {/* Filters header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="time-tab-group">
          {["This Month", "Previous Month", "This Year"].map((tab) => (
            <button
              key={tab}
              className={`time-tab ${activeFilter === tab ? "active" : ""}`}
              onClick={() => setActiveFilter(tab)}
              disabled={loading}
            >
              {tab}
            </button>
          ))}
        </div>
        <button className="btn-filter-report" onClick={() => setShowModal(true)}>
          <img src={filter1} alt="filter" />
          <span className="btn-filter-reporttext">Filters</span>
        </button>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
        </div>
      ) : (
        <div className="row g-3 mb-4">
          {stats.map((s, idx) => (
            <div className="col-md-4 col-12" key={idx}>
              <div className="stats-card d-flex justify-content-between">
                <div>
                  <h5 className="report-push-card">{s.value.toLocaleString()}</h5>
                  <p className="report-push-cardvalue mt-3">{s.label}</p>
                </div>
                <div className="stats-icon-wrapper">
                  <img src={s.icon} width={44} height={44} alt={s.label} className="stats-icon" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Breakdown */}
      <div className="reports-breakdown">
        <h6 className="mb-4 mt-4 push-report-breakdown">
          Reports Breakdown ({activeFilter})
        </h6>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
          </div>
        ) : breakdown.length === 0 ? (
          <div className="text-center py-5">
            <p className="text-muted">No reports found for the selected period</p>
          </div>
        ) : (
          breakdown.map((b, idx) => (
            <div
              className={`report-card mb-3 ${openIndex === idx ? "open" : ""}`}
              key={idx}
            >
              {/* Whole header clickable */}
              <div
                className="report-header"
                onClick={() =>
                  setOpenIndex(openIndex === idx ? null : idx)
                }
                style={{ cursor: "pointer" }}
              >
                {/* Title & Dropdown */}
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="report-push-title">{b.title}</h6>
                  <img
                    src={openIndex === idx ? down : up}
                    width={34}
                    height={34}
                    alt="toggle"
                    className="report-toggle"
                  />
                </div>

                {/* Notification info */}
                <p className="mb-1">
                  <span className="report-push-notifytitle">
                    Notification Title:
                  </span>{" "}
                  <span className="report-push-notifyvalue">{b.notification}</span>
                  <br />
                  <span className="report-push-notifytitle">Sent To:</span>{" "}
                  <span className="report-push-notifyvalue">{b.sentTo}</span>
                  <br />
                </p>

                {/* Success rate & Date */}
                <div className="d-flex justify-content-between align-items-center">
                  <span className="success-text">
                    <span className="report-push-notifytitle">Success Rate:</span>{" "}
                    <span className="report-push-notifyrate">{b.rate || b.successRate}</span>
                  </span>
                  <small className="d-flex align-items-center text-muted">
                    <img
                      src={reportclock}
                      width={11}
                      height={11}
                      className="me-1"
                      alt="clock"
                    />
                    {b.date}
                  </small>
                </div>
              </div>

              {/* Expanded Details */}
              {openIndex === idx && (
                <div className="mt-4">
                  <div style={{marginTop:"20px"}}>
                    <hr/>
                    <div className="report-details row" style={{marginTop:"40px"}}>
                      {/* API returns stats as an object; build tiles like the mobile app. Fallback data already uses an array. */}
                      {(Array.isArray(b.stats || b.details) ? (b.stats || b.details) : [
                        { label: "Push Sent", value: b.stats?.pushSent || 0, image: push },
                        { label: "Delivered", value: b.stats?.delivered || 0, image: pushdeliver },
                        { label: "Clicked", value: b.stats?.opened || 0, image: pushopen },
                        { label: "Seen", value: b.stats?.clicked || 0, image: pushclicked },
                        { label: "Registrations", value: b.stats?.registrations || 0, image: pushsubscription },
                      ]).map((d, i) => (
                        <div key={i} className="col-md-4 col-12 mb-3">
                          <div className="d-flex align-items-center justify-content-center">
                            <img
                              src={d.image || d.icon}
                              alt={d.label}
                              className="me-2 breakdown-icon"
                            />
                            <span>
                              {d.label}: <strong>{typeof d.value === 'number' ? d.value.toLocaleString() : d.value}</strong>
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Filter Modal (Dashboard Style) */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered dialogClassName="custom-filter-modal2">
        <div className="filter-modal-content">
          <div className="filter-title mb-4">Filter Reports</div>
          <button className="filter-close-btn" onClick={() => setShowModal(false)}>
            <img src={close} alt="close" />
          </button>

          {/* Date Range */}
          <label className="filter-label mt-3 mb-3">Date Range</label>
          <div className="filter-date-row">
            <div className="filter-date-field" onClick={(e) => openDatePicker("startDate", e)}>
              <span>{filters.startDate || "From"}</span>
              <img src={calendar} alt="calendar" className="calendar-icon" />
            </div>
            <div className="filter-date-field" onClick={(e) => openDatePicker("endDate", e)}>
              <span>{filters.endDate || "To"}</span>
              <img src={calendar} alt="calendar" className="calendar-icon" />
            </div>
          </div>

          {/* Report Type */}
          <label className="filter-label mt-4 mb-3">Report Type</label>
          <div className="filter-checkbox-group">
            {["Agent", "Agency", "Builder"].map((type) => (
              <div key={type} className="filter-checkbox">
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={filters.reportType.includes(type)}
                  onChange={() => toggleReportType(type)}
                />
                <label className="push-notify-report">{type}</label>
              </div>
            ))}
          </div>

          {/* Success Rate */}
          <label className="filter-label mt-4 mb-3">Success Rate</label>
          <div className="filter-dropdown">
            <Form.Select
              value={filters.successRate}
              onChange={(e) => setFilters(prev => ({ ...prev, successRate: e.target.value }))}
              onClick={() => toggleDropdown("successRate")}
            >
              <option value="">Select Success rate range</option>
              <option value="High (>10%)">High (&gt;10%)</option>
              <option value="Medium (5-10%)">Medium (5-10%)</option>
              <option value="Low (<5%)">Low (&lt;5%)</option>
              <option value="All">All</option>
            </Form.Select>
            <img src={openDropdown === "successRate" ? up : down} alt="dropdown" className="dropdown-icon" />
          </div>

          <div className="filter-footer">
            <button className="btn-clear2" onClick={handleClearFilters}>
              Clear Filters
            </button>
            <button className="btn-apply2" onClick={applyFilters}>
              Apply
            </button>
          </div>

          {/* Custom Date Picker Portal */}
          {isDatePickerVisible &&
            createPortal(
              <>
                <div className="datepicker-fullscreen" onClick={() => setIsDatePickerVisible(false)}></div>
                <div
                  className="datepicker-overlay"
                  style={{
                    top: pickerPosition.top,
                    left: pickerPosition.left,
                    width: pickerPosition.width,
                    position: "absolute",
                  }}
                >
                  <div className="datepicker-box">
                    <div className="selection-line m-3"></div>

                    {/* Day */}
                    <div className="datepicker-col" ref={dayRef} onScroll={() => handleScroll(dayRef, setSelectedDayIdx, days.length)}>
                      <div className="datepicker-item spacer"></div>
                      {days.map((d, i) => (
                        <div key={i} className={`datepicker-item ${i === selectedDayIdx ? "selected" : ""}`}>
                          {d}
                        </div>
                      ))}
                      <div className="datepicker-item spacer"></div>
                    </div>

                    {/* Month */}
                    <div className="datepicker-col" ref={monthRef} onScroll={() => handleScroll(monthRef, setSelectedMonthIdx, months.length)}>
                      <div className="datepicker-item spacer"></div>
                      {months.map((m, i) => (
                        <div key={i} className={`datepicker-item ${i === selectedMonthIdx ? "selected" : ""}`}>
                          {m}
                        </div>
                      ))}
                      <div className="datepicker-item spacer"></div>
                    </div>

                    {/* Year */}
                    <div className="datepicker-col" ref={yearRef} onScroll={() => handleScroll(yearRef, setSelectedYearIdx, years.length)}>
                      <div className="datepicker-item spacer"></div>
                      {years.map((y, i) => (
                        <div key={i} className={`datepicker-item ${i === selectedYearIdx ? "selected" : ""}`}>
                          {y}
                        </div>
                      ))}
                      <div className="datepicker-item spacer"></div>
                    </div>

                    <div className="datepicker-actions">
                      <button className="cancel-btn" onClick={() => setIsDatePickerVisible(false)}>
                        Cancel
                      </button>
                      <button className="save-btn" onClick={handleSaveDate}>
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </>,
              document.body
            )}
        </div>
      </Modal>
    </div>
  );
};
export default PushNotificationReports;